import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';

const SettingsContainer = styled.div`
  padding: 0 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SettingsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const SettingsSection = styled.div`
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 25px;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: var(--primary-color);
    border-bottom: 2px solid var(--gradient-pink);
    padding-bottom: 10px;
`;

const SettingItem = styled.div`
    margin-bottom: 20px;
    
    label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: var(--primary-color);
    }
    
    input, textarea {
        width: 100%;
        padding: 10px;
        background: rgba(0, 0, 0, 0.3);
        border: 1px solid var(--border-color);
        border-radius: 8px;
        color: var(--primary-color);
        font-size: 1rem;
        
        &:focus {
            outline: none;
            border-color: var(--gradient-pink);
        }
    }
    
    textarea {
        min-height: 80px;
        resize: vertical;
    }
`;

const SaveButton = styled.button`
    padding: 12px 30px;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: var(--gradient-pink);
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(255, 0, 127, 0.3);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const SuccessMessage = styled.div`
    background: rgba(46, 213, 115, 0.2);
    border: 1px solid #2ed573;
    color: #2ed573;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
`;

const AdminFooterPage = () => {
    const [footerSettings, setFooterSettings] = useState({
        footer_logo: 'CineGlass',
        footer_description: 'En yeni filmleri, dizileri ve animeleri HD kalitede izleyin. Türkçe dublaj ve altyazı seçenekleriyle zengin içerik kütüphanesi.',
        footer_copyright: '© 2025 CineGlass',
        footer_copyright_text: 'Tüm hakları saklıdır.',
        footer_made_with: 'Made with ❤️ by CineGlass Team',
        social_github: '',
        social_discord: '',
        social_twitter: '',
        social_instagram: '',
        social_youtube: '',
        social_facebook: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');

    useEffect(() => {
        loadFooterSettings();
    }, []);

    const loadFooterSettings = async () => {
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.GET_SETTINGS));
            if (response.ok) {
                const data = await response.json();
                const footerData = Object.keys(data)
                    .filter(key => key.startsWith('footer_') || key.startsWith('social_'))
                    .reduce((obj, key) => {
                        obj[key] = data[key] || footerSettings[key];
                        return obj;
                    }, {});
                
                setFooterSettings(prev => ({ ...prev, ...footerData }));
            }
        } catch (error) {
            console.error('Footer ayarları yüklenirken hata:', error);
        }
    };

    const handleSettingChange = (key, value) => {
        setFooterSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const saveFooterSettings = async () => {
        setLoading(true);
        setSaveStatus('');
        
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.SAVE_SETTINGS), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(footerSettings)
            });

            if (response.ok) {
                setSaveStatus('success');
                setTimeout(() => setSaveStatus(''), 3000);
            } else {
                throw new Error('Kayıt başarısız');
            }
        } catch (error) {
            console.error('Footer ayarları kaydedilirken hata:', error);
            setSaveStatus('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <SettingsContainer>
            <Header>
                <Title>Footer Ayarları</Title>
            </Header>

            {saveStatus === 'success' && (
                <SuccessMessage>
                    ✅ Footer ayarları başarıyla kaydedildi!
                </SuccessMessage>
            )}

            {saveStatus === 'error' && (
                <div style={{
                    background: 'rgba(255, 71, 87, 0.2)',
                    border: '1px solid #ff4757',
                    color: '#ff4757',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px'
                }}>
                    ❌ Footer ayarları kaydedilirken hata oluştu!
                </div>
            )}

            <SettingsGrid>
                <SettingsSection>
                    <SectionTitle>Genel Footer Ayarları</SectionTitle>
                    
                    <SettingItem>
                        <label>Footer Logo</label>
                        <input
                            type="text"
                            value={footerSettings.footer_logo}
                            onChange={(e) => handleSettingChange('footer_logo', e.target.value)}
                            placeholder="CineGlass"
                        />
                    </SettingItem>
                    
                    <SettingItem>
                        <label>Footer Açıklama</label>
                        <textarea
                            value={footerSettings.footer_description}
                            onChange={(e) => handleSettingChange('footer_description', e.target.value)}
                            placeholder="Site açıklaması..."
                        />
                    </SettingItem>
                    
                    <SettingItem>
                        <label>Telif Hakkı</label>
                        <input
                            type="text"
                            value={footerSettings.footer_copyright}
                            onChange={(e) => handleSettingChange('footer_copyright', e.target.value)}
                            placeholder="© 2025 CineGlass"
                        />
                    </SettingItem>

                    <SettingItem>
                        <label>Telif Hakkı Açıklaması</label>
                        <input
                            type="text"
                            value={footerSettings.footer_copyright_text}
                            onChange={(e) => handleSettingChange('footer_copyright_text', e.target.value)}
                            placeholder="Tüm hakları saklıdır."
                        />
                    </SettingItem>

                    <SettingItem>
                        <label>Footer Alt Yazısı</label>
                        <input
                            type="text"
                            value={footerSettings.footer_made_with}
                            onChange={(e) => handleSettingChange('footer_made_with', e.target.value)}
                            placeholder="Made with ❤️ by CineGlass Team"
                        />
                    </SettingItem>
                </SettingsSection>

                <SettingsSection>
                    <SectionTitle>Sosyal Medya Linkleri</SectionTitle>
                    
                    <SettingItem>
                        <label>GitHub URL</label>
                        <input
                            type="url"
                            value={footerSettings.social_github}
                            onChange={(e) => handleSettingChange('social_github', e.target.value)}
                            placeholder="https://github.com/username"
                        />
                        <small style={{ color: 'var(--secondary-color)', fontSize: '0.8rem' }}>
                            Boş bırakırsanız gözükmez
                        </small>
                    </SettingItem>

                    <SettingItem>
                        <label>Discord URL</label>
                        <input
                            type="url"
                            value={footerSettings.social_discord}
                            onChange={(e) => handleSettingChange('social_discord', e.target.value)}
                            placeholder="https://discord.gg/invite"
                        />
                        <small style={{ color: 'var(--secondary-color)', fontSize: '0.8rem' }}>
                            Boş bırakırsanız gözükmez
                        </small>
                    </SettingItem>

                    <SettingItem>
                        <label>Twitter/X URL</label>
                        <input
                            type="url"
                            value={footerSettings.social_twitter}
                            onChange={(e) => handleSettingChange('social_twitter', e.target.value)}
                            placeholder="https://x.com/username"
                        />
                        <small style={{ color: 'var(--secondary-color)', fontSize: '0.8rem' }}>
                            Boş bırakırsanız gözükmez
                        </small>
                    </SettingItem>

                    <SettingItem>
                        <label>Instagram URL</label>
                        <input
                            type="url"
                            value={footerSettings.social_instagram}
                            onChange={(e) => handleSettingChange('social_instagram', e.target.value)}
                            placeholder="https://instagram.com/username"
                        />
                        <small style={{ color: 'var(--secondary-color)', fontSize: '0.8rem' }}>
                            Boş bırakırsanız gözükmez
                        </small>
                    </SettingItem>

                    <SettingItem>
                        <label>YouTube URL</label>
                        <input
                            type="url"
                            value={footerSettings.social_youtube}
                            onChange={(e) => handleSettingChange('social_youtube', e.target.value)}
                            placeholder="https://youtube.com/@channel"
                        />
                        <small style={{ color: 'var(--secondary-color)', fontSize: '0.8rem' }}>
                            Boş bırakırsanız gözükmez
                        </small>
                    </SettingItem>

                    <SettingItem>
                        <label>Facebook URL</label>
                        <input
                            type="url"
                            value={footerSettings.social_facebook}
                            onChange={(e) => handleSettingChange('social_facebook', e.target.value)}
                            placeholder="https://facebook.com/page"
                        />
                        <small style={{ color: 'var(--secondary-color)', fontSize: '0.8rem' }}>
                            Boş bırakırsanız gözükmez
                        </small>
                    </SettingItem>
                </SettingsSection>
            </SettingsGrid>

            <div style={{ marginTop: '30px', textAlign: 'center' }}>
                <SaveButton onClick={saveFooterSettings} disabled={loading}>
                    {loading ? 'Kaydediliyor...' : 'Footer Ayarlarını Kaydet'}
                </SaveButton>
            </div>
        </SettingsContainer>
    );
};

export default AdminFooterPage;
